import { AlertCircle, PhoneOff } from 'lucide-react';
import { useId, useState } from 'react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { CopingTypesField } from '@/features/clients/components/shared/CopingTypesField';
import {
  isRiskProfile,
  RISK_UNSET,
  RiskProfileSelect,
  riskLabel,
  type RiskPick,
} from '@/features/clients/components/shared/RiskProfileSelect';
import { useUpdateCopingTypes } from '@/features/clients/hooks/useCopingTypes';
import type { RiskProfile } from '@/features/clients/types/schemas';
import { ApiError } from '@/lib/errors';
import { ConcernsMultiSelect } from '@/features/sessions/components/shared/ConcernsMultiSelect';
import type { ConcernKey } from '@/features/sessions/constants/concerns';

interface Props {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  /** Concerns already on the session (filled at booking time, if any). When
   *  this is non-empty the dialog skips the picker — concerns gate has been
   *  satisfied — and just confirms "End for everyone?".
   *  When empty the picker renders and a min-1 selection is required before
   *  Submit unblocks. */
  existingConcerns: ConcernKey[];
  /** Whether this role must categorise the session before completing. Defaults
   *  to true (psychologist / chief). Volunteers (listeners) don't diagnose, so
   *  the caller passes false: the concerns picker is hidden and Submit is never
   *  gated on a concern selection. Mirrors the BE gate, which exempts volunteers. */
  requireConcerns?: boolean;
  isPending: boolean;
  /** When true, render the "risk profile" selector. Restricted to
   *  psychologist + chief_psychologist (volunteers see the dialog but not the
   *  selector). Caller is responsible for the role check and for passing
   *  false on sessions with no client to profile. */
  canSetRisk?: boolean;
  /** Current risk on the client — used to seed the selector and label "Undefined"
   *  when null. Ignored when `canSetRisk` is false. When null (no risk on
   *  record yet) picking one becomes mandatory before Submit unblocks; the BE
   *  enforces the same rule. */
  currentRisk?: RiskProfile | null;
  /** Set for psychologists and chiefs on a session with a client: shows the
   *  coping types picker and saves it against this session on confirm. */
  coping?: { clientId: string; sessionId: string };
  /** Caller decides what to do with the picked concerns and (optional) risk.
   *  `risk` is only present when the practitioner explicitly changed it. */
  onConfirm: (input: { concerns: ConcernKey[]; risk?: RiskProfile }) => Promise<void> | void;
}

/**
 * Practitioner-side confirm for "Leave & complete". Two shapes:
 *  - existingConcerns has items → simple destructive confirm.
 *  - existingConcerns is empty → embeds ConcernsMultiSelect with `required`.
 *
 * Closing without confirming does nothing — the call stays live. Mirrors
 * the ask: "if they didn't fill, nothing will happen".
 */
export function LeaveAndCompleteDialog({
  open,
  onOpenChange,
  existingConcerns,
  requireConcerns = true,
  isPending,
  canSetRisk = false,
  currentRisk = null,
  coping,
  onConfirm,
}: Props) {
  const concernsId = useId();
  const riskId = useId();
  const [picked, setPicked] = useState<ConcernKey[]>(existingConcerns);
  const [pickedRisk, setPickedRisk] = useState<RiskPick>(currentRisk ?? RISK_UNSET);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [copingTypes, setCopingTypes] = useState<string[] | null>(null);
  const saveCopingTypes = useUpdateCopingTypes(coping?.clientId ?? '');

  // Reset transient state every time the dialog re-opens. Uses the React
  // docs' "store info from previous renders" pattern instead of useEffect
  // to satisfy react-hooks/set-state-in-effect.
  const [prevOpen, setPrevOpen] = useState(open);
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (open) {
      setPicked(existingConcerns);
      setPickedRisk(currentRisk ?? RISK_UNSET);
      setSubmitError(null);
      setCopingTypes(null);
    }
  }

  // Volunteers (listeners) don't categorise: requireConcerns=false hides the
  // picker entirely and never blocks Submit, regardless of what's on the row.
  const needsConcerns = requireConcerns && existingConcerns.length === 0;
  // A client with no risk on record must get one before the session closes
  // (`unknown` counts). Once a risk exists, the selector is optional.
  const needsRisk = canSetRisk && currentRisk === null;
  // When concerns weren't booked-in, gate Submit on a non-empty picker. When
  // concerns are already on the row (or this role is exempt), Submit is
  // unblocked from the start. Same shape for the risk gate.
  const concernsOk = needsConcerns ? picked.length > 0 : true;
  const riskOk = needsRisk ? isRiskProfile(pickedRisk) : true;
  const canSubmit = concernsOk && riskOk;

  const currentRiskLabel = riskLabel(currentRisk);

  async function handleConfirm() {
    setSubmitError(null);
    // Inline narrowing via the `isRiskProfile` type guard: `pickedRisk` is a
    // `RiskPick` union and TS only narrows it inside this expression — a
    // stored `riskChanged` const wouldn't propagate the narrowing into the
    // ternary value position below.
    const newRisk: RiskProfile | undefined =
      canSetRisk && isRiskProfile(pickedRisk) && pickedRisk !== currentRisk
        ? pickedRisk
        : undefined;
    if (coping && copingTypes) saveCopingTypes.mutate({ copingTypes, sessionId: coping.sessionId });
    try {
      await onConfirm({ concerns: picked, risk: newRisk });
    } catch (err) {
      setSubmitError(err instanceof ApiError ? err.message : 'Could not complete the session.');
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-title-md inline-flex items-center gap-2">
            <PhoneOff className="text-error-600 h-4 w-4" strokeWidth={1.75} aria-hidden />
            Leave and complete?
          </DialogTitle>
          <DialogDescription className="text-body-sm leading-relaxed text-neutral-600">
            {needsConcerns
              ? 'This ends the call for everyone and marks the session completed. Pick at least one concern this session covered before completing.'
              : 'This ends the call for everyone and marks the session completed.'}
          </DialogDescription>
        </DialogHeader>

        {needsConcerns ? (
          <div className="mt-2 flex flex-col gap-2">
            <label htmlFor={concernsId} className="text-meta-sm text-accent-ink font-medium">
              Concerns covered
            </label>
            <ConcernsMultiSelect
              id={concernsId}
              value={picked}
              onChange={setPicked}
              required
              placeholder="Pick at least one concern"
            />
          </div>
        ) : null}

        {canSetRisk ? (
          <div className="mt-2 flex flex-col gap-2">
            <div className="flex items-baseline justify-between gap-3">
              <label htmlFor={riskId} className="text-meta-sm text-accent-ink font-medium">
                Risk profile{' '}
                <span className="text-neutral-400">{needsRisk ? '(required)' : '(optional)'}</span>
              </label>
              <span className="text-meta-sm text-neutral-500">
                Current: <span className="text-accent-ink font-medium">{currentRiskLabel}</span>
              </span>
            </div>
            {needsRisk ? (
              <p className="text-meta-sm text-neutral-500">
                This client has no risk profile yet. Pick one, or Unknown, to complete.
              </p>
            ) : null}
            <RiskProfileSelect
              id={riskId}
              value={pickedRisk}
              onChange={setPickedRisk}
              placeholder={needsRisk ? 'Pick a risk profile' : 'Leave unchanged'}
            />
          </div>
        ) : null}

        {coping ? (
          <CopingTypesField
            clientId={coping.clientId}
            value={copingTypes}
            onChange={setCopingTypes}
          />
        ) : null}

        {submitError ? (
          <div
            role="alert"
            className="border-error-300/60 bg-error-50/85 text-error-700 mt-2 flex items-start gap-2 rounded-md border px-3 py-2"
          >
            <AlertCircle className="mt-px size-4 shrink-0" strokeWidth={1.75} aria-hidden />
            <p className="text-body-sm flex-1">{submitError}</p>
          </div>
        ) : null}

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            size="md"
            onClick={() => {
              onOpenChange(false);
            }}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            size="md"
            loading={isPending}
            disabled={!canSubmit || isPending}
            onClick={() => {
              void handleConfirm();
            }}
          >
            {isPending ? 'Completing…' : 'Leave & complete'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
